import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Layout, Menu, message, Skeleton, Space, Tooltip } from 'antd'
import { StarFilled, StarOutlined } from '@ant-design/icons'
import store from 'store'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { cloneDeep, find, isEmpty, isEqual } from 'lodash-es'
import { checkAlurFlow } from 'app-redux/options/selectors'
import { toggleSidebarApp } from 'app-redux/menu/actions'
import { logout } from 'app-redux/user/actions'
import { AppConsumer } from 'components/AppProvider'
import { DemoWarning, SubscriptionWarning, TrialWarning } from 'components/UI'
import GuideWidget from 'containers/FinanceOnboarding/GuideWidget'
import useNavigation from 'utils/hooks/useNavigation'
import useMemoizeCheckPermission from 'utils/hooks/useMemoizeCheckPermission'
import useUrlCrm from 'utils/hooks/useUrlCrm'
import useUrlGajihub from 'utils/hooks/useUrlGajihub'
import styles from './style.module.scss'
import ApprovalInfo from './ApprovalInfo'
import { SearchIcon, ToggleIcon } from './icons'
import { compareWithJSON } from '@/utils/helper'
import {
  billingSelector,
  darkModeSelector,
  loadedInitSelector,
  locationSelector,
  marketplaceConnectsSelector,
  menuLeftDataSelector,
  optionsSelector,
  settingsSelector,
  sidebarAppsSelector,
  userSelector,
} from '@/redux/selectors'

const { Sider } = Layout
const HIDDEN_MENUS = []

// Kembalikan rantai key semua ancestor sebuah item (root -> parent terdekat) untuk 3 level.
const findAncestorKeys = (nodes, targetKey, trail = []) => {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]
    if (node.key === targetKey) {
      return trail
    }
    if (Array.isArray(node.children)) {
      const found = findAncestorKeys(node.children, targetKey, [...trail, node.key])
      if (found) {
        return found
      }
    }
  }
  return null
}

// Popup tooltip harus ke body: ConfigProvider global menaruh popup di #scrollable-container (konten
// utama) sehingga tooltip bintang ter-clip / terlempar ke area halaman. Ke body -> buka ke sidebar.
const getTooltipContainer = () => document.body

// Bintang on/off aplikasi di sidebar. Terisi (amber) = app ON (di top-level); outline = OFF (di grup
// "Aplikasi Lainnya"). Klik -> toggle optimistic di redux (menu langsung berpindah) + PUT ke BE.
// Karena optimistic (state berubah seketika), tak perlu loader/disable saat simpan ke API.
// Diposisikan absolut supaya TIDAK mengubah layout baris (judul/ikon tetap seperti tanpa bintang).
const StarToggle = ({ appName }) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const sidebarApps = useSelector(sidebarAppsSelector)

  const groupedApps = useMemo(() => sidebarApps?.grouped_apps || [], [sidebarApps])
  const isGrouped = groupedApps.includes(appName)

  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation()
      e.preventDefault()
      try {
        // Optimistic: menu langsung berpindah dari redux; PUT & rollback ditangani thunk.
        const res = await dispatch(toggleSidebarApp(appName))
        message.success(res?.data?.message || formatMessage({ id: 'menu.sidebar_apps_saved' }))
      } catch (err) {
        // Error 400/403 sudah ditampilkan interceptor axios; state sudah di-rollback thunk.
      }
    },
    [appName, dispatch, formatMessage],
  )

  const tooltip = isGrouped
    ? formatMessage({ id: 'menu.sidebar_apps_show_main' })
    : formatMessage({ id: 'menu.sidebar_apps_move_group' })

  return (
    <Tooltip title={tooltip} placement="left" getPopupContainer={getTooltipContainer}>
      <button
        type="button"
        className={styles.starToggle}
        aria-label={tooltip}
        onClick={handleClick}
      >
        {isGrouped ? <StarOutlined /> : <StarFilled style={{ color: '#f0a83c' }} />}
      </button>
    </Tooltip>
  )
}

// Buang divider yatim (di awal, di akhir, atau berturut-turut) dari array antd-item
// setelah item difilter permission — supaya tidak muncul garis pemisah tanpa isi.
const pruneDividers = (items) => {
  const result = []
  items.forEach((item) => {
    if (!item) {
      return
    }
    if (item.type === 'divider') {
      const prev = result[result.length - 1]
      if (!prev || prev.type === 'divider') {
        return
      }
    }
    result.push(item)
  })
  while (result.length > 0 && result[result.length - 1].type === 'divider') {
    result.pop()
  }
  return result
}

const MENU_ICON_BASE = 'resources/images/icons/menu-v2'
const FALLBACK_MENU_ICON = `${MENU_ICON_BASE}/default.svg`

// Nama menu yang punya file ikon khusus (top-level) -> dipakai apa adanya.
const NAMED_MENU_ICONS = new Set([
  'home',
  'sales',
  'purchases',
  'expenses',
  'products',
  'approval',
  'finance_approval',
  'inventory',
  'reports',
  'cash_bank',
  'accounts',
  'fixed_assets',
  'contacts',
  'payroll',
  'crm',
  'pos',
  'distribution',
  'zap',
  'zap_settings',
  'marketplace',
  'membership',
  'other_apps',
  'settings',
  'faq',
  'warehouses',
  'material_requests',
  'material_issues',
])

// Peta konsep -> kata kunci (name + title, ID & EN). Urutan penting: spesifik dulu.
// Dipakai supaya tiap child menu punya ikon berbeda walau slug dari backend tak dikenal.
const CONCEPT_ICONS = [
  ['monitor', ['web pos', 'webpos', 'pos web']],
  ['download', ['download', 'unduh']],
  ['star', ['favorit', 'favourite', 'favorite']],
  ['checkin', ['check in', 'check-in', 'checkin', 'absen', 'kehadiran']],
  ['member', ['member', 'anggota', 'membership']],
  ['cube', ['produk master', 'product master', 'master produk', 'master product']],
  ['box', ['produk marketplace', 'marketplace product', 'produk mp']],
  ['megaphone', ['canvassing', 'kanvasing', 'kanvas', 'promosi', 'campaign']],
  ['route', ['rute', 'route', 'jalur']],
  ['truck', ['pengiriman', 'kirim', 'armada', 'fleet', 'delivery', 'ekspedisi', 'pengantaran']],
  ['document', ['penawaran', 'quote', 'quotation', 'proposal']],
  ['return', ['retur', 'return', 'pengembalian', 'refund']],
  ['gift', ['benefit', 'hadiah', 'reward', 'manfaat', 'bonus']],
  ['grid', ['overview', 'ringkasan', 'ikhtisar', 'dashboard']],
  ['store', ['outlet', 'toko', 'store', 'marketplace ku', 'my marketplace', 'gerai']],
  ['cart', ['pesanan', 'order', 'pemesanan', 'keranjang']],
  [
    'receipt',
    [
      'transaksi',
      'transaction',
      'tagihan',
      'faktur',
      'invoice',
      'struk',
      'nota',
      'pembayaran',
      'payment',
      'rekap',
    ],
  ],
  ['user', ['pengguna', 'cashier', 'kasir', 'user']],
  ['tag', ['label', 'tag']],
  ['faq', ['faq', 'bantuan', 'help']],
  ['gear', ['pengaturan', 'setting', 'konfigurasi', 'kelola']],
]

// Tentukan slug ikon: nama khusus -> cocok kata kunci -> default.
const resolveMenuIconSlug = (name, title) => {
  const namedSlug = (name || '').toLowerCase()
  if (NAMED_MENU_ICONS.has(namedSlug)) {
    return namedSlug
  }
  const haystack = `${name || ''} ${title || ''}`.replace(/_/g, ' ').toLowerCase()
  const matched = CONCEPT_ICONS.find(([, keywords]) =>
    keywords.some((keyword) => haystack.includes(keyword)),
  )
  return matched ? matched[0] : 'default'
}

const getInitials = (name) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()

// Ikon menu ilustratif dari file SVG (pola sama seperti settings-v2), fallback ke default.
// Slug dipilih dari name + title supaya tiap child menu tampil beda, bukan default semua.
const MenuIcon = ({ name, title }) => {
  const handleError = useCallback((event) => {
    if (!event.currentTarget.src.endsWith('default.svg')) {
      event.currentTarget.src = FALLBACK_MENU_ICON
    }
  }, [])

  const slug = resolveMenuIconSlug(name, title)

  return (
    <img
      className={styles.icon}
      src={`${MENU_ICON_BASE}/${slug}.svg`}
      alt=""
      onError={handleError}
    />
  )
}

/**
 * Buka aplikasi eksternal (gajihub / crm) di tab baru dengan token terbaru.
 * Tab harus dibuka sinkron (sebelum await) supaya tidak diblokir popup blocker.
 */
const openExternalApp = async (e, getFreshUrl) => {
  e.preventDefault()
  const tab = window.open('about:blank', '_blank')
  const url = await getFreshUrl()

  // Sesi sudah tidak valid - modal login sudah ditampilkan oleh error handler axios
  if (!url) {
    tab?.close()
    return
  }

  if (tab) {
    tab.opener = null
    tab.location.replace(url)
  } else {
    window.open(url, '_blank', 'noopener')
  }
}

const MenuLeft = ({ scrollBarRef, appContext }) => {
  const menuData = useSelector(menuLeftDataSelector)
  const { isMobileView } = useSelector(settingsSelector)
  const darkMode = useSelector(darkModeSelector)
  const property = useSelector(optionsSelector)
  const user = useSelector(userSelector)
  const billing = useSelector(billingSelector)
  const translationMenu = useSelector((state) => state.translation.menu)
  const loadedInit = useSelector(loadedInitSelector)
  const location = useSelector(locationSelector)
  const { urlGajihub, getFreshUrlGajihub } = useUrlGajihub()
  const { urlCrm, getFreshUrlCrm } = useUrlCrm()
  const hasMarketplaceConnect = useMemoizeCheckPermission({ permission: 'marketplace_connect' })
  const marketplaceConnects = useSelector(marketplaceConnectsSelector)
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const { onNavigate } = useNavigation()

  const [selectedKeys, setSelectedKeys] = useState(() => store.get('app.menu.selectedKeys') || [])
  const [openedKeys, setOpenedKeys] = useState(() => store.get('app.menu.openedKeys') || [])
  const [searchQuery, setSearchQuery] = useState('')

  const searchInputRef = useRef(null)
  const prevAppContextRef = useRef()
  const prevMenuDataRef = useRef()
  const prevLocationRef = useRef()

  const isMenuCollapsed = appContext?.settings?.isMenuCollapsed

  // Daftar app yang boleh di-toggle on/off (dari BE). Dipakai untuk memutuskan baris mana yang
  // menampilkan bintang. Grouping "Aplikasi Lainnya" sepenuhnya dibentuk BE (bukan lagi client-side).
  const sidebarApps = useSelector(sidebarAppsSelector)
  const availableApps = useMemo(
    () => (sidebarApps?.available_apps || []).map((app) => (app || '').toLowerCase()),
    [sidebarApps],
  )

  const flattenItems = useCallback((items, key) => {
    const flatten = (itemsToFlatten) =>
      itemsToFlatten.reduce((flattenedItems, item) => {
        flattenedItems.push(item)
        if (Array.isArray(item[key])) {
          return flattenedItems.concat(flatten(item[key]))
        }
        return flattenedItems
      }, [])
    return flatten(items)
  }, [])

  const setSelectedKeysFunc = useCallback(
    (preserveOpen = false) => {
      const pathName = location.pathname === '/dashboard' ? '/' : location.pathname
      const flattenData = flattenItems(menuData, 'children')
      let selectedItem = null
      if (pathName === '/') {
        selectedItem = find(flattenData, { url: pathName })
      } else if (pathName.startsWith('/settings')) {
        selectedItem = find(menuData, { name: 'settings' })
      } else if (pathName.startsWith('/reports')) {
        selectedItem = find(menuData, { name: 'reports' })
      } else if (pathName === '/pos/roles') {
        const posMenu = find(flattenData, { name: 'pos' })
        selectedItem = find(posMenu?.children, { name: 'pos_cashier_users' })
      } else if (pathName === '/pos/web-pos') {
        selectedItem = find(flattenData, { url: '/cashier' })
      } else if (pathName.startsWith('/zap/settings')) {
        // Path ini juga mengandung '/settings', jadi loop di bawah akan salah pilih Pengaturan global
        selectedItem = find(flattenData, { name: 'zap_settings' })
      } else {
        for (let i = 0; i < flattenData.length; i += 1) {
          const item = flattenData[i]

          if (pathName.includes('/pos/settings') && item.url?.includes('/pos/settings')) {
            selectedItem = item
            break
          }

          // Pilih url yang paling spesifik: /membership/settings juga mengandung /settings,
          // jadi item yang lebih pendek (Pengaturan global) tidak boleh menimpanya.
          if (
            item.url &&
            item.url !== '/' &&
            pathName.includes(item.url) &&
            item.url.length >= (selectedItem?.url?.length || 0)
          ) {
            selectedItem = item
          }
        }
      }

      store.set('app.menu.selectedKeys', selectedItem ? [selectedItem.key] : [])
      setSelectedKeys(selectedItem ? [selectedItem.key] : [])

      // preserveOpen: menu berubah tanpa pindah rute (mis. toggle bintang menyusun ulang grup).
      // Pertahankan submenu yang sedang dibuka user — jangan tutup "Aplikasi Lainnya" — dan hanya
      // buang key yang node-nya sudah tak ada di menu baru.
      if (preserveOpen) {
        const existingKeys = new Set(flattenData.map((item) => item.key))
        setOpenedKeys((prev) => {
          const kept = (prev || []).filter((key) => existingKeys.has(key))
          store.set('app.menu.openedKeys', kept)
          return kept
        })
        return
      }

      // Sinkronkan submenu terbuka dengan rute aktif: buka seluruh rantai ancestor item
      // aktif (mendukung 3 level). Di dashboard (tanpa parent) -> semua tertutup.
      const ancestors = selectedItem ? findAncestorKeys(menuData, selectedItem.key) || [] : []
      store.set('app.menu.openedKeys', ancestors)
      setOpenedKeys(ancestors)
    },
    [menuData, location, flattenItems],
  )

  const onCollapse = useCallback(
    (value, type) => {
      const { onChangeSettings, settings: { isMenuCollapsed: collapsed } = {} } = appContext
      if (type === 'responsive' && collapsed) {
        return
      }

      onChangeSettings({ isMenuCollapsed: value })
    },
    [appContext],
  )

  const handleToggleCollapse = useCallback(() => {
    const { onChangeSettings, settings: { isMenuCollapsed: collapsed } = {} } = appContext
    onChangeSettings({ isMenuCollapsed: !collapsed })
  }, [appContext])

  const onOpenChange = useCallback((newOpenedKeys) => {
    setOpenedKeys(newOpenedKeys)
  }, [])

  const handleClick = useCallback((e) => {
    // keyPath = [key item, ...ancestor]. Buka semua ancestor supaya menu 3 level
    // tetap terbuka saat memilih item terdalam.
    const openKeys = e.keyPath.slice(1)
    store.set('app.menu.selectedKeys', [e.key])
    store.set('app.menu.openedKeys', openKeys)

    setSelectedKeys([e.key])
    setOpenedKeys(openKeys)
  }, [])

  // Popup submenu harus ke body: ConfigProvider menaruh popup di #scrollable-container
  // (konten utama), sehingga flyout saat collapse ter-clip dan klik terasa diam.
  const getMenuPopupContainer = useCallback(() => document.body, [])

  const renderScrollView = useCallback(({ style, ...restProps }) => {
    return (
      <div
        {...restProps}
        style={{
          ...style,
          marginRight: 0,
          marginBottom: 0,
        }}
      />
    )
  }, [])

  const renderTrackVertical = useCallback(
    ({ style, ...restProps }) => {
      return (
        <div
          {...restProps}
          style={{
            ...style,
            right: 2,
            bottom: 2,
            top: 2,
            width: 4,
            ...(isMenuCollapsed ? { display: 'none' } : {}),
          }}
        />
      )
    },
    [isMenuCollapsed],
  )

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value)
  }, [])

  const handleSearchBoxClick = useCallback(() => {
    if (isMenuCollapsed) {
      handleToggleCollapse()
      return
    }
    searchInputRef.current?.focus()
  }, [isMenuCollapsed, handleToggleCollapse])

  const handleLogout = useCallback(async () => {
    await dispatch(logout())
    onNavigate('/user/login')
  }, [dispatch, onNavigate])

  const handleOpenGajihub = useCallback(
    (e) => openExternalApp(e, getFreshUrlGajihub),
    [getFreshUrlGajihub],
  )

  const handleOpenCrm = useCallback((e) => openExternalApp(e, getFreshUrlCrm), [getFreshUrlCrm])

  // Filter menu berdasarkan pencarian: pertahankan parent bila judul cocok atau ada anak cocok.
  const filteredMenuData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) {
      return menuData
    }
    const filterList = (list) =>
      (list || []).reduce((acc, item) => {
        if (item.divider) {
          return acc
        }
        const selfMatch = (item.title || '').toLowerCase().includes(query)
        if (Array.isArray(item.children)) {
          const kids = filterList(item.children)
          if (selfMatch || kids.length) {
            acc.push({ ...item, children: kids.length ? kids : item.children })
          }
        } else if (selfMatch) {
          acc.push(item)
        }
        return acc
      }, [])
    return filterList(menuData)
  }, [menuData, searchQuery])

  const searchOpenKeys = useMemo(() => {
    if (!searchQuery.trim()) {
      return null
    }
    return flattenItems(filteredMenuData, 'children')
      .filter((item) => Array.isArray(item.children) && item.children.length)
      .map((item) => item.key)
  }, [searchQuery, filteredMenuData, flattenItems])

  const menuOpenProps = useMemo(() => {
    if (isMenuCollapsed) {
      return {}
    }
    return {
      openKeys: searchOpenKeys || openedKeys,
      onOpenChange,
    }
  }, [isMenuCollapsed, searchOpenKeys, openedKeys, onOpenChange])

  const menuItems = useMemo(() => {
    const _menuData = cloneDeep(filteredMenuData || []).filter((menu) => !isEmpty(menu))
    const menu = _menuData.map((item) => {
      if (item.name === 'settings') {
        item.children = null
      }

      return { ...item }
    })

    const materialMenuItems = [
      {
        key: 'material_requests-inventory',
        name: 'material_requests',
        title:
          translationMenu?.material_requests || formatMessage({ id: 'menu.material_requests' }),
        url: '/warehouses/material-requests',
        icon: 'tags',
      },
      {
        key: 'material_issues-inventory',
        name: 'material_issues',
        title: translationMenu?.material_issues || formatMessage({ id: 'menu.material_issues' }),
        url: '/warehouses/material-issues',
        icon: 'tags',
      },
    ]

    // Child "Gudang" saat modul ON tapi data BE masih bentuk OFF (link tunggal, belum refresh).
    const buildGudangChild = (inventoryItem) => ({
      key: 'warehouses-inventory',
      name: 'warehouses',
      title: translationMenu?.warehouses || formatMessage({ id: 'menu.warehouses' }),
      url: inventoryItem.url || '/warehouses',
      icon: inventoryItem.icon || 'warehouse',
    })

    // Bentuk menu "Inventori" mengikuti opsi `material_module_enabled` secara render-time
    // (pola sama seperti `invoice_order`) supaya berubah tanpa refresh. Sesuai BE:
    //   modul OFF -> Inventori berupa link tunggal ke /warehouses (tanpa sub).
    //   modul ON  -> Inventori berupa submenu [Gudang, Permintaan Material, Pengeluaran Material].
    const transformInventoryItem = (item) => {
      const materialOn = !!property?.material_module_enabled
      const hasChildren = Array.isArray(item.children) && item.children.length > 0

      if (materialOn) {
        if (hasChildren) {
          // Data BE sudah bentuk ON: tambahkan submenu material yang belum ada saja.
          const missing = materialMenuItems.filter(
            (materialItem) => !item.children.some((child) => child.name === materialItem.name),
          )
          return { ...item, url: undefined, children: [...item.children, ...missing] }
        }
        // Data BE masih bentuk OFF (link) tapi baru di-toggle ON: bangun submenu + Gudang.
        return { ...item, url: undefined, children: [buildGudangChild(item), ...materialMenuItems] }
      }

      // Modul OFF -> Inventori harus link tunggal tanpa anak.
      if (hasChildren) {
        const warehousesChild = item.children.find((child) => child.name === 'warehouses')
        return {
          ...item,
          url: warehousesChild?.url || item.url || '/warehouses',
          children: undefined,
        }
      }
      return item
    }

    // Child "Permintaan Pembelian" untuk disisipkan saat data BE (cache inits) belum memuatnya —
    // supaya menu langsung muncul begitu Alur Bisnis diaktifkan, tanpa nunggu refresh cache 48 jam.
    // Visibilitas tetap digate checkAlurFlow('purchase_requests') sesuai opsi purchase_request.
    const purchaseRequestMenuItem = {
      key: 'purchase_requests-purchases',
      name: 'purchase_requests',
      title: translationMenu?.purchase_requests || formatMessage({ id: 'menu.purchase_requests' }),
      url: '/purchases/purchaserequests',
      icon: 'tag',
    }

    // Sisipkan "Permintaan Pembelian" ke submenu Pembelian bila BE belum mengirimnya (pola sama
    // seperti transformInventoryItem untuk modul material). Kalau BE sudah memuatnya, biarkan apa
    // adanya agar tidak dobel.
    const transformPurchasesItem = (item) => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0
      if (!hasChildren) {
        return item
      }
      const exists = item.children.some((child) => child.name === 'purchase_requests')
      if (exists) {
        return item
      }
      return { ...item, children: [...item.children, purchaseRequestMenuItem] }
    }

    // Petakan item menu ke token available_apps (case-insensitive). Fallback membership: BE kadang
    // mengirim `name` item dashboard berbeda dari token available_apps -> cocokkan lewat judul.
    const resolveAppName = (item) => {
      const name = (item.name || '').toLowerCase()
      if (availableApps.includes(name)) {
        return name
      }
      if (availableApps.includes('membership')) {
        const title = (item.title || '').toLowerCase()
        if (title.includes('membership') || title.includes('keanggotaan')) {
          return 'membership'
        }
      }
      return null
    }

    // Sisipkan bintang on/off di kanan baris — hanya untuk app yang bisa di-toggle (dari BE) dan saat
    // sidebar tidak collapsed. Bintang absolut & di luar Link/anchor: layout baris TIDAK berubah dan
    // klik bintang tak ikut navigasi.
    const buildLabel = (item, inner) => {
      const appName = isMenuCollapsed ? null : resolveAppName(item)
      if (!appName) {
        return inner
      }
      return (
        <>
          {inner}
          <StarToggle appName={appName} />
        </>
      )
    }

    const generateItem = (item) => {
      const { key, title, disabled } = item
      let { url } = item
      if (url === '/cashier') {
        url = '/pos/web-pos'
      }
      const id = `kledo-menu-${item.name}`
      if (item.divider) {
        return {
          type: 'divider',
          key: `divider-${key}`,
        }
      }

      const icon =
        item.name === 'finance_approval' ? (
          <span className={styles.iconWrap}>
            <MenuIcon name={item.name} title={title} />
            {isMenuCollapsed && <ApprovalInfo isMenuCollapsed />}
          </span>
        ) : (
          <MenuIcon name={item.name} title={title} />
        )

      // CRM eksternal (WhatsApp) -> buka tab baru dgn token fresh.
      if (item.name === 'crm' && property?.option?.app_connect_wa) {
        return {
          id,
          key,
          disabled,
          icon,
          label: buildLabel(
            item,
            <a
              href={urlCrm}
              onClick={handleOpenCrm}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.titleLink}
            >
              <span className={styles.title}>{title}</span>
            </a>,
          ),
        }
      }

      // Payroll eksternal (GajiHub) -> buka tab baru dgn token fresh.
      if (item.name === 'payroll' && property?.option?.app_connect_hr) {
        return {
          id,
          key,
          disabled,
          icon,
          label: buildLabel(
            item,
            <a
              href={urlGajihub}
              onClick={handleOpenGajihub}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.titleLink}
            >
              <span className={styles.title}>{title}</span>
            </a>,
          ),
        }
      }

      if (item.target && url) {
        return {
          id,
          key,
          disabled,
          icon,
          label: buildLabel(
            item,
            <a
              href={url}
              target={item.target}
              rel="noopener noreferrer"
              className={styles.titleLink}
            >
              <span className={styles.title}>{title}</span>
            </a>,
          ),
        }
      }

      if (url) {
        return {
          id,
          key,
          disabled,
          icon,
          label: buildLabel(
            item,
            <Link to={url} className={styles.titleLink}>
              <span className={styles.title}>{title}</span>
              {item.name === 'finance_approval' && !isMenuCollapsed && <ApprovalInfo />}
            </Link>,
          ),
        }
      }

      return {
        key,
        disabled,
        icon,
        label: buildLabel(item, <span className={styles.title}>{title}</span>),
      }
    }

    const generateSubmenu = (items) => {
      return pruneDividers(
        items.map((menuItem) => {
          if (HIDDEN_MENUS.includes(menuItem.name)) {
            return null
          }
          if (!checkAlurFlow(menuItem.name)) {
            return null
          }
          const id = `kledo-menu-${menuItem.name}`

          if (menuItem.children) {
            return {
              key: menuItem.key,
              id,
              icon: <MenuIcon name={menuItem.name} title={menuItem.title} />,
              label: buildLabel(menuItem, menuItem.title),
              children: generateSubmenu(menuItem.children),
            }
          }
          return generateItem(menuItem)
        }),
      )
    }

    return pruneDividers(
      menu.map((rawItem) => {
        if (rawItem.name === 'marketplace' && !hasMarketplaceConnect) {
          return null
        }
        let menuItem = rawItem
        if (rawItem.name === 'inventory') {
          menuItem = transformInventoryItem(rawItem)
        } else if (rawItem.name === 'purchases') {
          menuItem = transformPurchasesItem(rawItem)
        } else if (rawItem.name === 'marketplace' && !marketplaceConnects?.length) {
          menuItem = { ...rawItem, url: '/marketplace/landing', children: undefined }
        }
        // Judul grup "Aplikasi Lainnya" diterjemahkan dari redux translation menu (inits) seperti
        // menu lain; fallback ke locale react-intl. (Reducer hanya menaruh id/fallback statis.)
        if (menuItem.name === 'other_apps') {
          menuItem.title = translationMenu?.other_apps || formatMessage({ id: 'menu.other_apps' })
        }
        const id = `kledo-menu-${menuItem.name}`
        if (menuItem.children) {
          return {
            key: menuItem.key,
            id,
            icon: <MenuIcon name={menuItem.name} title={menuItem.title} />,
            label: buildLabel(
              menuItem,
              !isEmpty(menuItem.url) ? (
                <Link to={menuItem.url} className={styles.titleLink}>
                  <span className={styles.title}>{menuItem.title}</span>
                </Link>
              ) : (
                menuItem.title
              ),
            ),
            children: generateSubmenu(menuItem.children),
          }
        }

        return generateItem(menuItem)
      }),
    )
  }, [
    filteredMenuData,
    property,
    translationMenu,
    formatMessage,
    urlGajihub,
    handleOpenGajihub,
    urlCrm,
    handleOpenCrm,
    isMenuCollapsed,
    hasMarketplaceConnect,
    marketplaceConnects,
    availableApps,
  ])

  const makeLoader = useCallback(() => {
    const loaders = []
    for (let i = 1; i <= 10; i += 1) {
      loaders.push(
        <Space key={i}>
          <Skeleton.Avatar active size="small" shape="circle" />
          {!isMenuCollapsed && <Skeleton.Input style={{ width: 185 }} active size="small" />}
        </Space>,
      )
    }
    return loaders
  }, [isMenuCollapsed])

  // useEffect untuk componentDidUpdate
  useEffect(() => {
    if (
      prevAppContextRef.current?.settings?.isMenuCollapsed !== appContext?.settings?.isMenuCollapsed
    ) {
      if (appContext?.settings?.isMenuCollapsed && !isMobileView) {
        setOpenedKeys([])
      }
    }
    prevAppContextRef.current = appContext
  }, [appContext, isMobileView])

  useEffect(() => {
    const menuChanged = !isEqual(prevMenuDataRef.current, menuData)
    const locationChanged = prevLocationRef.current?.pathname !== location.pathname
    if (menuChanged || locationChanged) {
      // Menu berubah tanpa pindah rute & bukan render awal (mis. toggle bintang) -> pertahankan
      // submenu yang sedang dibuka user, jangan reset open key dari rute aktif.
      const preserveOpen = menuChanged && !locationChanged && prevMenuDataRef.current != null
      setSelectedKeysFunc(preserveOpen)
    }
    prevMenuDataRef.current = menuData
    prevLocationRef.current = location
  }, [menuData, location, setSelectedKeysFunc])

  const menuSettings = useMemo(() => {
    return isMobileView
      ? {
          width: 256,
          collapsible: false,
          collapsed: false,
          collapsedWidth: 0,
          trigger: null,
          onCollapse,
        }
      : {
          width: 256,
          collapsible: true,
          collapsed: isMenuCollapsed,
          trigger: null,
          onCollapse,
          breakpoint: 'lg',
        }
  }, [isMenuCollapsed, isMobileView, onCollapse])

  const showWarnings = !isMenuCollapsed || isMobileView

  return (
    <Sider
      {...menuSettings}
      className={darkMode ? styles.menu : `${styles.menu} ${styles.light}`}
      style={{ zIndex: 4 }}
    >
      <div className={styles.logo}>
        <div className={styles.logoLead}>
          <Link to="/" className={styles.logoLink}>
            <span
              className={
                isEmpty(property.property_logo)
                  ? styles.logoMark
                  : `${styles.logoMark} ${styles.logoMarkImg}`
              }
            >
              {!isEmpty(property.property_logo) ? (
                <img src={property.property_logo} alt={property.property_name} />
              ) : (
                getInitials(property.property_name)
              )}
            </span>
            <span className={styles.logoText}>
              <b>{property.property_name || 'Kledo'}</b>
              <span className={styles.logoPlan}>{billing?.plan || ''}</span>
            </span>
          </Link>
        </div>
        {!isMobileView && (
          <button
            type="button"
            className={styles.toggle}
            onClick={handleToggleCollapse}
            aria-label="Ciutkan menu"
          >
            <ToggleIcon />
          </button>
        )}
      </div>

      <GuideWidget collapsed={isMenuCollapsed && !isMobileView} />

      <div className={styles.search}>
        <div className={styles.searchBox} onClick={handleSearchBoxClick} role="presentation">
          <input
            ref={searchInputRef}
            className={styles.searchInput}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={formatMessage({
              id: 'menu.search_placeholder',
              defaultMessage: 'Cari menu',
            })}
          />
          <SearchIcon className={styles.searchIc} />
        </div>
      </div>

      {showWarnings && [
        <SubscriptionWarning key="subscription" showIcon={showWarnings} />,
        <DemoWarning key="demo" showIcon={showWarnings} />,
        <TrialWarning key="trial" showIcon={showWarnings} />,
      ]}

      <Scrollbars
        ref={scrollBarRef}
        className={isMobileView ? styles.scrollbarMobile : styles.scrollbarDesktop}
        renderView={renderScrollView}
        renderTrackVertical={renderTrackVertical}
        renderThumbVertical={({ style, ...restProps }) => (
          <div
            {...restProps}
            style={{
              ...style,
              width: '4px',
              borderRadius: 'inherit',
              backgroundColor: '#c5cdd2',
              left: '1px',
            }}
          />
        )}
        autoHide
      >
        {!loadedInit ? (
          <Space className="mt-5 ml-4" direction="vertical" size={30}>
            {makeLoader()}
          </Space>
        ) : (
          <Menu
            theme="light"
            onClick={handleClick}
            selectedKeys={selectedKeys}
            {...menuOpenProps}
            mode="inline"
            inlineIndent={14}
            triggerSubMenuAction={isMenuCollapsed ? 'hover' : 'click'}
            getPopupContainer={getMenuPopupContainer}
            style={{ overflowX: 'hidden' }}
            className={styles.navigation}
            items={menuItems}
          />
        )}
      </Scrollbars>

      <div className={styles.foot}>
        <div className={styles.userCard}>
          <Link to="/settings/profile" className={styles.userMain}>
            <span className={styles.avatar}>
              {!isEmpty(user?.profile_image) ? (
                <img src={user.profile_image} alt={user?.name} />
              ) : (
                getInitials(user?.name)
              )}
              <span className={styles.online} />
            </span>
            <span className={styles.uMeta}>
              <span className={styles.uName}>{user?.name || 'Pengguna'}</span>
              <span className={styles.uRole}>{property.property_name}</span>
            </span>
          </Link>
          <button
            type="button"
            className={styles.logoutBtn}
            data-tip={formatMessage({ id: 'topBar.profileMenu.logout' })}
            aria-label={formatMessage({ id: 'topBar.profileMenu.logout' })}
            onClick={handleLogout}
          >
            <img src={`${MENU_ICON_BASE}/action-logout.svg`} alt="" />
          </button>
        </div>
      </div>
    </Sider>
  )
}

const MenuLeftComponent = (props) => {
  return <AppConsumer>{(context) => <MenuLeft {...props} appContext={context} />}</AppConsumer>
}

export default memo(MenuLeftComponent, compareWithJSON)
